import type { Plugin } from '#app'
import { StatusCode } from 'grpc-web';

import { useStoreAccount } from '../stores/store.account'
import { rootEmitter } from './03.emitter'
import { maxLogLevel } from './01.logger'

import { ErrorCode } from '~/api/myedvenn/common';
import { AuthorizedHeadersInterceptor, EmptyFieldCallInterceptor } from '~/api/util/grpc.client';
import type { GrpcWebRequest, GrpcWebResponse, GrpcWebUnaryInterceptor } from '~/api/util/grpc.client';
import { AccountServiceClient } from '~/api/myedvenn/account.client';
import { UserServiceClient } from '~/api/myedvenn/user.client';
import { ContactServiceClient } from '~/api/myedvenn/contact.client';
import { AttendanceServiceClient } from '~/api/myedvenn/attendance.client';
import { PaymentServiceClient } from '~/api/myedvenn/payment.client';
import { DocumentServiceClient } from '~/api/myedvenn/document.client';
import { CommentServiceClient } from '~/api/myedvenn/comment.client';
import { PackageServiceClient } from '~/api/myedvenn/packages.client';
import { PackageFileServiceClient } from '~/api/myedvenn/package_file.client';
import { ContactPackageAssignServiceClient, ContactPackageServiceClient } from '~/api/myedvenn/contact_package.client';
import { LeadServiceClient, ReportServiceClient } from '~/api/myedvenn/report.client';

const HEADER_ACCESS = "authorization" as const
const HEADER_REFRESH = "authorization-refresh" as const
const HEADER_EMPTY_ARRAY = "tolemac-empty-array"
const HEADER_EMPTY_FIELD = "tolemac-empty-field"
const AUTHORIZED_HEADERS = [HEADER_ACCESS, HEADER_REFRESH, HEADER_EMPTY_ARRAY, HEADER_EMPTY_FIELD] as const

type Metadata = {
    [key in typeof AUTHORIZED_HEADERS[number]]?: string;
}
type MetadataInternal = Metadata & {
    skipErrorCode?: number | number[];

    /** disabled add token on request */
    skipToken?: boolean;

    // /** force refresh token */
    // refreshToken?: true;
}

class AuthUnaryInterceptor implements GrpcWebUnaryInterceptor<MetadataInternal> {

    private refreshingToken = false

    get storeAccount() {
        return useStoreAccount()
    }

    async intercept(request: GrpcWebRequest<MetadataInternal>, invoker: (r: GrpcWebRequest<MetadataInternal>) => Promise<GrpcWebResponse<MetadataInternal>>) {
        const start = new Date().getTime()
        try {
            const refreshing = this.addToken(request)

            const response = await invoker(request)

            this.logReq(request, response, undefined, new Date().getTime() - start )

            this.receiveToken(response.getMetadata(), refreshing)

            return response;
        } catch (e: any) {

            const code = parseInt((e?.metadata && e.metadata['error-code']) || `${e?.code}` || '2')
            const codeGroup = code % 1000

            e.code = code
            e.codeGroup = codeGroup

            this.logReq(request, undefined, e, new Date().getTime() - start)

            let skipErrorCode = request.getMetadata().skipErrorCode
            if (skipErrorCode) {
                skipErrorCode = Array.isArray(skipErrorCode) ? skipErrorCode : [skipErrorCode]
                if (skipErrorCode.indexOf(codeGroup) !== -1) return
            }

            if (code === ErrorCode.UNAUTHORIZED_LOGOUT) {
                this.logout()
            } else if (codeGroup >= 400 && codeGroup < 500) {
                rootEmitter.notifyWarn(e)
            } else if (code === StatusCode.UNAVAILABLE) {
                rootEmitter.notifyError("Service temporairement indisponible")
            } else if (code === StatusCode.UNKNOWN || codeGroup >= 500) {
                rootEmitter.notifyError(e)
            }

            throw e;
        }
    }

    private logReq(request: GrpcWebRequest<MetadataInternal>, response?: GrpcWebResponse<MetadataInternal>, error?: any, time?: number) {
        const method = request.getMethodDescriptor().getName();

        if (error) {
            console.info(`[API] ${method} throw an error.${msToDuration(time)}`)
        } else {
            console.info(`[API] ${method}${msToDuration(time)}`)
            if (maxLogLevel < 3) return
        }

        const req = request.getRequestMessage().toObject(false)
        if (req && Object.keys(req).length !== 0)
            if (error)
                console.warn('\treq:', req)
            else
                console.debug('\treq:', req)

        if (error) {
            // `${code}${code !== codeGroup ? ' ' + codeGroup : ''} ${e?.message || ''}`
            console.warn('\terror:', error)
        } else {
            const rep = response.getResponseMessage().toObject(false)
            if (rep && Object.keys(rep).length !== 0)
                console.debug('\trep:', rep)
        }
    }

    /**
     * add token on request
     */
    private addToken(req: GrpcWebRequest<MetadataInternal>) {
        const meta = req.getMetadata()

        if (meta.skipToken) return

        const token = this.storeAccount.$state.token;
        if (token && (token.refresh || token.access)) {
            const now = Date.now() + 2
            // if access token is expired, use refresh token
            if (token.accessDecoded?.exp && now > token.accessDecoded.exp) {
                // if refresh token is expired, logout
                if (token.refreshDecoded?.exp && now > token.refreshDecoded.exp)
                    this.logout()
                else
                    if (!this.refreshingToken) {
                        meta[HEADER_REFRESH] = 'Bearer ' + token.refresh;
                        this.refreshingToken = true
                        return true
                    } else {
                        meta[HEADER_REFRESH] = 'Bearer ' + token.refresh;
                        meta[HEADER_ACCESS] = 'Bearer ' + token.access;
                    }
            } else {
                meta[HEADER_ACCESS] = 'Bearer ' + token.access;
            }
        } else {
            console.info(req.getMethodDescriptor().getName(), 'no token')
        }
    }

    /**
     * - login: store access + refresh JWT token
     * - refreshing: update stored access JWT token
     */
    private receiveToken(repMetadata: Metadata, refreshing?: boolean) {
        // login
        if (!this.storeAccount.token && repMetadata[HEADER_ACCESS] && repMetadata[HEADER_REFRESH])
            this.storeAccount.setToken(repMetadata[HEADER_ACCESS], repMetadata[HEADER_REFRESH]);

        // refresh access token
        else if (refreshing)
            if (repMetadata[HEADER_ACCESS]) {
                this.storeAccount.setToken(repMetadata[HEADER_ACCESS]);
                this.refreshingToken = false
            } else {
                console.warn('missing refreshed token', Object.keys(repMetadata))
                this.logout()
            }
    }

    private disconnecting = false

    private async logout() {
        if (this.disconnecting) return
        try {
            this.disconnecting = true
            this.refreshingToken = false
            rootEmitter.notifyWarn("Session expirée")
            await this.storeAccount.logout(true)
            throw new Error('Session expirée')
        } finally {
            setTimeout(() => this.disconnecting = false, 500)
        }
    }
}


function msToDuration(ms: number) {
    if (!ms) return ''
    const _s = Math.floor(ms / 1000)
    const _ms = ms % 1000
    return _s ? ` [${_s}s${_ms}ms]` : ` [${_ms}ms]`
}

class ApiHttp {

    get storeAccount() {
        return useNuxtApp().$store.account
    }

    /**
     * fetch:
     * - add authorization header
     */
    fetch(input: RequestInfo, options?: RequestInit) {

        const headers = {} as any;
        const token = this.storeAccount.$state.token;
        let refreshing = false
        if (token && (token.refresh || token.access)) {
            const now = Date.now() + 2
            // if access token is expired, use refresh token
            if (token.accessDecoded?.exp && now > token.accessDecoded.exp) {
                // if refresh token is expired, logout
                if (!(token.refreshDecoded?.exp && now > token.refreshDecoded.exp)) {
                    refreshing = true
                    headers[HEADER_REFRESH] = 'Bearer ' + token.refresh;
                }
                // else { this.logout() }
            } else {
                headers[HEADER_ACCESS] = 'Bearer ' + token.access;
            }
        }

        return fetch(input, { ...options, ...{ headers } })
            .then(async rep => {
                if (rep.status >= 300) throw new Error(await rep.text())
                if (refreshing)
                    if (rep.headers.has(HEADER_ACCESS)) {
                        this.storeAccount.setToken(rep.headers.get(HEADER_ACCESS))
                    } else {
                        console.warn('missing refreshed token', [...rep.headers.keys()])
                    }
                return rep
            })
            .finally(() => {
                refreshing = false
            })
    }

    // async upload(f: IFileLink, data: any) {
    //     if (data instanceof File) {
    //         const formData = new FormData();
    //         formData.set('file', data)
    //         formData.set('link', JSON.stringify(f))
    //         await this.fetch('file/upload', {
    //             method: 'POST',
    //             body: formData
    //         })
    //     }
    // }
}

const apiNuxtPlugin = function () {

    const grpcClientOptions = {
        callInterceptors: [
            new EmptyFieldCallInterceptor(HEADER_EMPTY_ARRAY, HEADER_EMPTY_FIELD)
        ],
        unaryInterceptors: [
            new AuthorizedHeadersInterceptor([...AUTHORIZED_HEADERS]),
            new AuthUnaryInterceptor()
        ]
    }

    const apiBaseUrl = useRuntimeConfig().public.apiBaseUrl || window.location.origin

    return {
        provide: {
            api: {
                account: new AccountServiceClient<MetadataInternal>(apiBaseUrl, grpcClientOptions),
                user: new UserServiceClient<MetadataInternal>(apiBaseUrl, grpcClientOptions),
                contact: new ContactServiceClient<MetadataInternal>(apiBaseUrl, grpcClientOptions),
                attendance: new AttendanceServiceClient<MetadataInternal>(apiBaseUrl, grpcClientOptions),
                payment: new PaymentServiceClient<MetadataInternal>(apiBaseUrl, grpcClientOptions),
                document: new DocumentServiceClient<MetadataInternal>(apiBaseUrl, grpcClientOptions),
                comment: new CommentServiceClient<MetadataInternal>(apiBaseUrl, grpcClientOptions),
                pack: new PackageServiceClient<MetadataInternal>(apiBaseUrl, grpcClientOptions),
                packageFile: new PackageFileServiceClient<MetadataInternal>(apiBaseUrl, grpcClientOptions),
                contactPackage: new ContactPackageServiceClient<MetadataInternal>(apiBaseUrl, grpcClientOptions),
                contactPackageAssign: new ContactPackageAssignServiceClient<MetadataInternal>(apiBaseUrl, grpcClientOptions),
                report: new ReportServiceClient<MetadataInternal>(apiBaseUrl, grpcClientOptions),
                leads: new LeadServiceClient<MetadataInternal>(apiBaseUrl, grpcClientOptions),

                http: new ApiHttp()
            }
        }
    }
} satisfies Plugin

export type Api = ReturnType<typeof apiNuxtPlugin>['provide']['api']


export default defineNuxtPlugin(apiNuxtPlugin)
